/* ============================================================
   Monsuri native cart drawer — behavior (2026-08-24, fix wave 2026-09-05)
   ------------------------------------------------------------
   Public API: two CustomEvents that already existed as dispatch
   sites in the theme (mobile-sticky-atc) with no listener:
     document.dispatchEvent(new CustomEvent('cart:refresh'))
     document.dispatchEvent(new CustomEvent('cart:open'))
   Every add path in the theme converges on these. Back-to-back
   dispatches in the same turn share ONE Section Rendering fetch.

   Rendering model: Liquid is the single renderer. After every
   mutation the drawer re-fetches its own section via the Section
   Rendering API (GET /?sections=cart-drawer) and swaps the inner
   HTML — money format, underscore-property filtering, discounts
   and bundle grouping are all server truth, never JS templating.
   Two things live OUTSIDE the swapped node and are updated field
   by field: the status line (so an error survives the re-render
   that follows every mutation) and the footer (Shopify's
   accelerated-checkout buttons are script-hydrated — a swap would
   re-insert them inert).

   Gift note: JS owns the model. The DOM is a view of it — a
   re-render re-applies the model, so a keystroke inside the save
   debounce can never be overwritten by the server's older note
   (the 2026-09-05 data-loss bug). The textarea is name="note" and
   every save syncs all other textarea[name="note"] on the page —
   the same contract pdp-gift-toggle.js enforces from the PDP side.

   The card lives ONLY in cart.note (2026-09-14): "To <name>," on the
   first line, then the words. No cart attributes — Shopify's
   Additional details land in ShipStation's Note From Buyer as a
   <br/>-joined duplicate of the note. The "Who is it for?" field is
   read back out of that first line (splitGiftNote; the same rule in
   sections/cart-drawer.liquid, gift-quiz.js and the dashboard sync).

   A11y: role=dialog + focus trap (bbox-based getFocusable — iOS
   returns null offsetParent inside position:fixed, WebKit 106162),
   Esc + scrim close, opener focus return, aria-live announcements
   INSIDE the dialog, iOS-safe body scroll lock (position:fixed
   pattern — the theme's body.blocked-scroll sets pointer-events:
   none and would kill the drawer itself).

   Init is split: document/window listeners bind ONCE per page and
   resolve the current drawer lazily; per-root wiring re-runs on
   shopify:section:load (the theme editor hands us a NEW root, and
   a per-element guard used to let every reload stack another copy
   of every global listener).
   ============================================================ */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var GA4_ID = 'G-04VG181C0C';
  var NOTE_DEBOUNCE_MS = 600;
  var CHECKOUT_FLUSH_TIMEOUT_MS = 1200;
  /* Retired order attributes: sent as null on every save so a bag started
     before 2026-09-14 cannot carry them to the order. */
  var RETIRED_GIFT_ATTRIBUTES = {
    'Gift Note — To': null, 'Gift Note — From': null, 'Gift Note': null,
    'Gift Occasion': null, 'Your Monsuri box': null
  };

  /* "To Maya,\nHappy birthday" → { to: 'Maya', message: 'Happy birthday' } */
  function splitGiftNote(note) {
    var s = String(note || '');
    var nl = s.indexOf('\n');
    var head = nl === -1 ? s : s.slice(0, nl);
    var m = /^To (.+),$/.exec(head.trim());
    var name = m ? m[1].trim() : '';
    if (!name || name.length > 40) return { to: '', message: s };
    return { to: name, message: nl === -1 ? '' : s.slice(nl + 1) };
  }
  function joinGiftNote(to, message) {
    var name = String(to || '').trim();
    var words = splitGiftNote(message).message; /* never stack two "To" lines */
    return name ? 'To ' + name + ',' + (words ? '\n' + words : '') : words;
  }

  /* Direct gtag with send_to — the ONLY path that reaches GA4 on this theme
     (the GTM container GTM-TPTGMCR never loads; dataLayer pushes are no-ops).
     Same pattern as mds-tracking.js / mds-promotions.js / gsbTrack.
     Elevar's web pixel owns add_to_cart / begin_checkout / purchase — the
     theme never emits those (GA4_MEASUREMENT_PLAN.md). */
  function track(eventName, params) {
    try {
      if (typeof window.gtag !== 'function') return;
      var p = params || {};
      p.send_to = GA4_ID;
      window.gtag('event', eventName, p);
    } catch (_) { /* analytics is best-effort */ }
  }

  /* The current drawer instance (one per page). Globals call through this so a
     theme-editor section reload swaps the instance without re-binding them. */
  var drawer = null;

  function createDrawer(root) {
    var scrim = root.querySelector('[data-cdw-scrim]');
    var panel = root.querySelector('[data-cdw]');
    var live = root.querySelector('[data-cdw-live]');
    if (!panel || !scrim) return null;

    var openerEl = null;
    var keydownHandler = null;
    var scrollLocked = false;
    var scrollY = 0;
    var mutationChain = Promise.resolve();
    var mutationEpoch = 0;
    var refreshTick = null;
    var refreshInFlight = null;   /* the current SRA promise, for event-driven reuse */
    var refreshStartedAt = 0;
    var EVENT_REUSE_MS = 400;
    var noteTimer = null;
    var notePending = false;
    var noteInFlight = null;
    var giftChain = Promise.resolve();
    var seenPromos = {}; /* impression dedupe: per OPEN session (cleared on close) */
    var giftSeq = 0;   /* bumps on every local gift edit AND every completed save */
    var saveSeq = 0;   /* tokens each saveGift so an OLDER save cannot clear a newer in-flight marker */
    var composing = false; /* IME (zh-CN): never rewrite the field mid-composition */
    var legacyGiftCleared = false; /* one migration save per page for a pre-2026-09-14 bag */

    /* The gift model — the single source of truth for the gift fields. */
    var gift = { seeded: false, sawDetails: false, note: '', to: '', open: false };

    /* ——— tiny utils ——— */
    function q(sel) { return root.querySelector(sel); }
    function isOpen() { return panel.classList.contains('is-open'); }
    function announce(text) {
      if (!live) return;
      live.textContent = '';
      window.setTimeout(function () { live.textContent = text; }, 60);
    }

    /* getFocusable — bbox check, not offsetParent (iOS/WebKit 106162) */
    function getFocusable() {
      var nodes = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])');
      var out = [];
      for (var i = 0; i < nodes.length; i++) {
        var r = nodes[i].getBoundingClientRect();
        if (r.width > 0 && r.height > 0) out.push(nodes[i]);
      }
      return out;
    }

    /* iOS-safe scroll lock (gift-set-builder pattern, idempotent) */
    function lockScroll(on) {
      var b = document.body;
      if (on) {
        if (scrollLocked) return;
        scrollLocked = true;
        scrollY = window.scrollY || window.pageYOffset || 0;
        b.style.position = 'fixed';
        b.style.top = (-scrollY) + 'px';
        b.style.left = '0';
        b.style.right = '0';
        b.style.width = '100%';
      } else {
        if (!scrollLocked) return;
        scrollLocked = false;
        b.style.position = '';
        b.style.top = '';
        b.style.left = '';
        b.style.right = '';
        b.style.width = '';
        window.scrollTo(0, scrollY);
      }
    }

    /* ——— status line (outside the swapped node — it survives refresh) ——— */
    function showStatus(message) {
      /* the node carries role="alert" — that IS the announcement; a second
         polite announce() would read the same error twice */
      var el = q('[data-cdw-status]');
      if (!el) { announce(message); return; }
      /* unhide FIRST, then insert the text — an alert only fires reliably for
         content inserted into an already-rendered live region */
      el.hidden = false;
      window.requestAnimationFrame(function () { el.textContent = message; });
    }
    function clearStatus() {
      var el = q('[data-cdw-status]');
      if (el) { el.textContent = ''; el.hidden = true; }
    }

    /* ——— open / close ——— */
    /* opts.trigger: 'icon' | 'add' | 'hover'. Hover-intent opens are a desktop
       convenience and are NOT counted as cart_drawer_opened — they inflated the
       open metric with mouse drift. */
    function open(opts) {
      var o = opts || {};
      if (isOpen()) { if (o.refresh) refreshFromEvent(); return; }
      openerEl = document.activeElement;
      scrim.hidden = false;
      panel.hidden = false;
      /* force a layout so the transition runs from the hidden state */
      void panel.offsetWidth;
      scrim.classList.add('is-open');
      panel.classList.add('is-open');
      lockScroll(true);
      keydownHandler = function (e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        var f = getFocusable();
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', keydownHandler);
      window.setTimeout(function () {
        var h = q('[data-cdw-title]');
        if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
      }, 60);
      if (o.trigger === 'hover') track('cart_drawer_hover_open', {});
      else track('cart_drawer_opened', { trigger: o.trigger || 'add' });
      trackImpression();
      syncExpress();
      if (o.refresh) refreshFromEvent();
    }

    function close() {
      if (!isOpen()) return;
      seenPromos = {};
      flushNote();
      scrim.classList.remove('is-open');
      panel.classList.remove('is-open');
      lockScroll(false);
      if (keydownHandler) { document.removeEventListener('keydown', keydownHandler); keydownHandler = null; }
      window.setTimeout(function () {
        if (!isOpen()) { scrim.hidden = true; panel.hidden = true; }
      }, 360);
      if (openerEl && openerEl.focus) { try { openerEl.focus(); } catch (_) {} }
      openerEl = null;
    }

    /* ——— server-truth re-render (Section Rendering API) ——— */
    /* Same-turn coalescing: every add path dispatches cart:refresh AND
       cart:open back-to-back, which used to cost two identical fetches per
       add. Calls inside one event-loop turn share a single request; a later
       mutation is a new turn and gets its own (the epoch guard discards the
       older response). */
    function refresh() {
      if (refreshTick) return refreshTick;
      refreshTick = Promise.resolve().then(function () {
        refreshTick = null;
        refreshStartedAt = Date.now();
        refreshInFlight = doRefresh();
        refreshInFlight.then(clearInFlight, clearInFlight);
        return refreshInFlight;
      });
      return refreshTick;
      function clearInFlight() { refreshInFlight = null; }
    }

    /* Event-driven refreshes (cart:refresh / cart:open from OUTSIDE the drawer)
       reuse a request that started within the last few hundred ms — app embeds
       have been seen dispatching their own cart:refresh ~150 ms before the
       theme's pair. Mutation-driven refreshes never take this path: a refresh
       issued before the mutation resolved would show stale contents. */
    function refreshFromEvent() {
      if (refreshInFlight && (Date.now() - refreshStartedAt) < EVENT_REUSE_MS) return refreshInFlight;
      return refresh();
    }

    function doRefresh() {
      var epoch = ++mutationEpoch;
      var giftSeqAtRequest = giftSeq;
      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(routeRoot + '?sections=' + SECTION_ID, { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (epoch !== mutationEpoch) return;             /* stale response — discard */
          var html = data && data[SECTION_ID];
          if (!html) { syncBadgeFromCartJs(); return; }    /* SRA null section — degrade */
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshInner = doc.querySelector('[data-cdw-inner]');
          var inner = q('[data-cdw-inner]');
          if (!freshInner || !inner) return;
          /* focus + caret continuity across the swap */
          var ae = document.activeElement;
          var focusKey = ae && ae.getAttribute ? ae.getAttribute('data-cdw-focus') : null;
          var caret = null;
          if (ae && ae.matches && ae.matches('[data-cdw-gift-note], [data-cdw-gift-to]')) {
            try { caret = { start: ae.selectionStart, end: ae.selectionEnd }; } catch (_) { caret = null; }
          }
          /* preserve the scroll position across the swap — a qty change must
             not teleport the buyer back to the top (Sol mobile #4) */
          var oldBody = q('.cdw__body');
          var oldScrollTop = oldBody ? oldBody.scrollTop : 0;
          inner.innerHTML = freshInner.innerHTML;
          applyFooter(doc);
          applyGiftModel(freshInner, giftSeqAtRequest);
          wireInner();
          var newBody = q('.cdw__body');
          if (newBody) {
            var maxTop = Math.max(0, newBody.scrollHeight - newBody.clientHeight);
            newBody.scrollTop = Math.min(oldScrollTop, maxTop);
          }
          if (focusKey) {
            var again = q('[data-cdw-focus="' + focusKey + '"]');
            if (again) {
              try { again.focus({ preventScroll: true }); } catch (_) {}
              if (caret && again.setSelectionRange) {
                try { again.setSelectionRange(caret.start, caret.end); } catch (_) {}
              }
            }
          }
          var count = freshInner.getAttribute('data-item-count');
          if (count !== null) updateBadges(count);
          /* the swap can land after open()'s focus timer targeted a node the
             swap replaced — focus falls back to <body>; recover to the title */
          if (isOpen()) {
            var ae2 = document.activeElement;
            if (!ae2 || ae2 === document.body) {
              var h2 = q('[data-cdw-title]');
              if (h2) { h2.setAttribute('tabindex', '-1'); h2.focus({ preventScroll: true }); }
            }
          }
        })
        .catch(function () {
          /* keep the current markup; server truth returns next mutation — but
             say so (unless a more specific error is already showing), and
             clear the busy state the swap would have replaced */
          var busy = root.querySelectorAll('[aria-busy="true"]');
          for (var i = 0; i < busy.length; i++) busy[i].removeAttribute('aria-busy');
          var st = q('[data-cdw-status]');
          if (!st || st.hidden) showStatus('We could not refresh your bag just now. Please try again.');
        });
    }

    /* Footer fields (subtotal / savings / empty-state visibility) are copied
       from the fresh section; the checkout form + express buttons are never
       re-created. */
    function applyFooter(doc) {
      var freshFoot = doc.querySelector('[data-cdw-foot]');
      var foot = q('[data-cdw-foot]');
      if (!freshFoot || !foot) { showStatus('We could not refresh your bag just now. Please try again.'); return; }
      foot.hidden = freshFoot.hasAttribute('hidden');
      var pairs = [['[data-cdw-subtotal]', false], ['[data-cdw-cta-amount]', false], ['[data-cdw-savings]', true]];
      for (var i = 0; i < pairs.length; i++) {
        var fresh = freshFoot.querySelector(pairs[i][0]);
        var cur = foot.querySelector(pairs[i][0]);
        if (!fresh || !cur) continue;
        cur.textContent = fresh.textContent;
        if (pairs[i][1]) cur.hidden = fresh.hasAttribute('hidden');
      }
      syncExpress();
    }

    /* Shopify's accelerated-checkout element only hydrates when its script was
       included at PAGE LOAD, which Shopify does only for a non-empty cart. On
       an empty-cart page load the markup is inert — so the "check out faster
       with" label would sit above nothing. Show the block only when the
       wallet element actually rendered; it appears on the next page view. */
    var expressWatched = false;
    function syncExpress() {
      var ex = q('[data-cdw-express]');
      if (!ex) return;
      var TAG = 'shopify-accelerated-checkout-cart';
      /* "live" = the custom element is defined AND has rendered content. Never
         measure height here — a hidden container measures 0 and would pin
         itself hidden forever. */
      var check = function () {
        var el = ex.querySelector(TAG);
        var live = !!(el && window.customElements && customElements.get(TAG) && (el.shadowRoot || el.children.length));
        ex.hidden = !live;
      };
      check();
      if (!expressWatched && window.customElements && typeof customElements.whenDefined === 'function') {
        expressWatched = true;
        /* Shopify defines the element a few seconds after load (async script) */
        customElements.whenDefined(TAG).then(function () {
          window.setTimeout(check, 250);
          window.setTimeout(check, 1500);
        }).catch(function () { /* never defined on this page — stays hidden */ });
      }
    }

    function updateBadges(count) {
      var badges = document.querySelectorAll('.cart_count');
      for (var i = 0; i < badges.length; i++) badges[i].textContent = String(count);
    }
    function syncBadgeFromCartJs() {
      fetch('/cart.js').then(function (r) { return r.json(); })
        .then(function (c) { updateBadges(c.item_count); })
        .catch(function () { /* no-op */ });
    }

    /* ——— serialized mutation queue ——— */
    function enqueue(fn) {
      mutationChain = mutationChain.then(fn, fn);
      mutationChain.catch(function () { /* surfaced by the mutation's own catch */ });
      return mutationChain;
    }

    /* every mutation ends the same way: a network failure is SHOWN, then the
       drawer reconciles to server truth (the optimistic qty would otherwise
       stay wrong on screen forever) */
    function networkFailed() {
      showStatus('Connection error — please try again.');
      return refresh();
    }

    function readError(res, fallback) {
      return res.json().then(function (d) {
        return (d && (d.description || d.message)) || fallback;
      }).catch(function () { return fallback; });
    }

    function changeLine(key, quantity) {
      clearStatus();
      return enqueue(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: quantity })
        }).then(function (res) {
          if (!res.ok) return readError(res, 'That change did not go through.').then(showStatus);
        }).then(refresh, networkFailed);
      });
    }

    function addVariant(variantId, label, meta) {
      /* meta = { handle, anchor, tier } from the pairing card — feeds BOTH the
         GA4 select_promotion event AND the hidden order-attribution stamps
         (_pair_*) the dashboard's line-item sync persists. */
      var m = meta || {};
      var item = { id: parseInt(variantId, 10), quantity: 1 };
      if (m.anchor && m.tier) {
        item.properties = {
          _pair_source: 'cart_drawer',
          _pair_anchor: m.anchor,
          _pair_tier: m.tier
        };
      }
      clearStatus();
      return enqueue(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [item] })
        }).then(function (res) {
          if (!res.ok) {
            /* reconcile to server truth via the single terminal refresh below;
               NO success events on failure */
            return readError(res, 'We could not add that just now.').then(showStatus);
          }
          if (m.anchor && m.handle) {
            track('select_promotion', {
              promotion_id: m.anchor + '>' + m.handle,
              promotion_name: label || m.handle,
              creative_slot: 'cart_drawer_' + (m.tier || 'unknown'),
              items: [{ item_id: m.handle, item_name: label || m.handle }]
            });
          }
          if (label) announce('Added — ' + label + '.');
        }).then(refresh, networkFailed);
      });
    }

    function removeKit(keys) {
      clearStatus();
      return enqueue(function () {
        /* ONE /cart/update.js with every key → 0 — atomic; a sequential loop
           can strand half a kit on a mid-stream failure (Codex Sol MED). */
        var updates = {};
        keys.forEach(function (key) { updates[key] = 0; });
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates: updates })
        }).then(function (res) {
          if (!res.ok) return readError(res, 'That change did not go through.').then(showStatus);
        }).then(refresh, networkFailed);
      });
    }

    /* ——— gift note: the model ——— */
    function seedGiftFromDom(scope) {
      var noteEl = scope.querySelector('[data-cdw-gift-note]');
      var toEl = scope.querySelector('[data-cdw-gift-to]');
      var det = scope.querySelector('[data-cdw-gift]');
      gift.note = noteEl ? noteEl.value : '';
      gift.to = toEl ? toEl.value : '';
      /* the disclosure only exists in the non-empty branch: seed its open state
         the first time it APPEARS, not the first time the drawer renders */
      if (!gift.sawDetails && det) { gift.open = det.hasAttribute('open'); gift.sawDetails = true; }
      gift.seeded = true;
    }

    /* After a swap the fresh markup carries the SERVER's gift fields. When a
       local edit is pending or in flight the model wins (that edit has not
       reached the server yet); otherwise the server is the truth and the model
       re-seeds from it (another surface — the PDP gift toggle — may have
       written the note). The disclosure's open state is always client-owned. */
    function applyGiftModel(freshInner, seqAtRequest) {
      /* Re-seed ONLY from a render requested after the last local edit and the
         last completed save (giftSeq unchanged since the request went out) and
         with nothing pending — otherwise the section snapshot predates a save
         and would blank the note the buyer just wrote (Opus lens-1 C1). */
      var stale = seqAtRequest !== undefined && seqAtRequest !== giftSeq;
      if (!gift.seeded || (!stale && !notePending && !noteInFlight)) {
        seedGiftFromDom(freshInner);
      } else if (!gift.sawDetails) {
        var d0 = freshInner.querySelector('[data-cdw-gift]');
        if (d0) { gift.open = d0.hasAttribute('open'); gift.sawDetails = true; }
      }
      var noteEl = q('[data-cdw-gift-note]');
      var toEl = q('[data-cdw-gift-to]');
      var det = q('[data-cdw-gift]');
      if (noteEl && !composing) noteEl.value = gift.note;
      if (toEl) toEl.value = gift.to;
      if (det) det.open = gift.open;
    }

    /* Only whole-note writers assign the card field without an input event (the
       PDP toggle, another surface's sync), so an assigned value is the truth for the
       name too. Absorb it before any local edit reads the model. `typingName` = the
       name field is the one being edited: its value is left alone (caret, IME). */
    function absorbAssignedNote(typingName) {
      var el = q('[data-cdw-gift-note]');
      if (!el || el.value === gift.note) return;
      var parts = splitGiftNote(el.value);
      gift.note = parts.message;
      gift.to = parts.to;
      /* the fields must show the split, or the next save re-reads the stale "To" line */
      if (!composing) el.value = parts.message;
      var toEl = q('[data-cdw-gift-to]');
      if (toEl && !typingName) toEl.value = parts.to;
    }

    /* A bag from before 2026-09-14 (retired attributes present): re-read the CART, not the
       page-load render (another tab may have written since), fold a name that lives only
       in `Gift Note — To` into the note, and clear every retired key. Serialized with the
       saves; runs once per page. */
    function migrateLegacyGift() {
      giftChain = giftChain.then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.ok ? res.json() : null; });
      }).then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        var legacy = Object.keys(RETIRED_GIFT_ATTRIBUTES).some(function (k) { return attrs[k]; });
        if (!legacy) return;
        var card = splitGiftNote(cart.note);
        var legacyTo = String(attrs['Gift Note \u2014 To'] || '').trim().slice(0, 40);
        var note = card.to ? String(cart.note || '') : joinGiftNote(legacyTo, card.message);
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ note: note, attributes: RETIRED_GIFT_ATTRIBUTES })
        }).then(function (res) {
          if (!res.ok) return;
          giftSeq++; /* a render requested before this write is stale */
          syncOtherNoteFields(note);
          if (hasPendingNote()) return; /* a local edit is newer — it saves over this */
          var parts = splitGiftNote(note);
          gift.note = parts.message;
          gift.to = parts.to;
          var noteEl = q('[data-cdw-gift-note]'), toEl = q('[data-cdw-gift-to]');
          if (noteEl && !composing) noteEl.value = gift.note;
          if (toEl && document.activeElement !== toEl) toEl.value = gift.to;
          refreshGiftPresentation();
        });
      }).catch(function () { /* best-effort: the attributes stay until the next page load */ });
    }

    function giftPayload() {
      absorbAssignedNote();
      /* attributes MERGE server-side: the retired keys are cleared explicitly */
      return { note: joinGiftNote(gift.to, gift.note), attributes: RETIRED_GIFT_ATTRIBUTES };
    }

    /* The card field holds only the words. A value that arrives with a whole "To <name>,"
       line (a note assigned by another surface, then typed into) carries the name — move
       it to the name field instead of letting the model's older name win. Needs the
       newline: "To Bob," mid-sentence is still just words. */
    function readTypedNote(el) {
      var v = el.value;
      var parts = v.indexOf('\n') !== -1 ? splitGiftNote(v) : { to: '', message: v };
      gift.note = parts.message;
      if (!parts.to) return;
      gift.to = parts.to;
      if (!composing) el.value = parts.message;
      var toEl = q('[data-cdw-gift-to]');
      if (toEl) toEl.value = parts.to;
    }

    function syncOtherNoteFields(note) {
      var fields = document.querySelectorAll('textarea[name="note"]');
      for (var i = 0; i < fields.length; i++) {
        if (!fields[i].hasAttribute('data-cdw-gift-note')) fields[i].value = note;
      }
    }

    function saveGift() {
      var payload = giftPayload();
      var myId = ++saveSeq;
      notePending = false;
      var savedEl = q('[data-cdw-gift-saved]');
      if (savedEl) { savedEl.textContent = 'Saving…'; savedEl.hidden = false; }
      /* Serialized on giftChain (Codex Sol HIGH, 2026-08-24): two overlapping
         update.js requests can complete out of order and restore a STALE
         note. Each save waits for the previous one; the payload is the model
         at execution time, so the LAST save always carries the newest values. */
      noteInFlight = giftChain = giftChain.then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });
      }).then(function (res) {
        if (saveSeq === myId) noteInFlight = null; /* a newer save owns the marker */
        giftSeq++;
        var el = q('[data-cdw-gift-saved]');
        if (el) { el.textContent = res.ok ? 'Saved' : 'Could not save — try again'; el.hidden = false; }
        if (res.ok) syncOtherNoteFields(payload.note);
      }).catch(function () {
        if (saveSeq === myId) noteInFlight = null;
        giftSeq++;
        var el = q('[data-cdw-gift-saved]');
        if (el) { el.textContent = 'Could not save — try again'; el.hidden = false; }
      });
      return noteInFlight;
    }

    function queueGiftSave() {
      giftSeq++;
      notePending = true;
      var savedEl = q('[data-cdw-gift-saved]');
      if (savedEl) { savedEl.textContent = 'Saving…'; savedEl.hidden = false; }
      if (noteTimer) window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(saveGift, NOTE_DEBOUNCE_MS);
    }

    function flushNote() {
      if (noteTimer) { window.clearTimeout(noteTimer); noteTimer = null; }
      if (notePending) return saveGift();
      return noteInFlight || Promise.resolve();
    }

    function hasPendingNote() { return !!(notePending || noteInFlight); }

    /* pagehide keepalive: a pending gift note must survive ANY navigation
       (checkout, view-cart, back) — the debounced save dies with the page. */
    function flushNoteKeepalive() {
      if (!hasPendingNote() || !gift.seeded) return;
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(giftPayload())
        });
      } catch (_) { /* best-effort */ }
    }

    /* ——— gift-mode presentation: the heading, the "For <name>" line on the
       card, the rail heading, the counter. The checkout button never changes:
       it reads "Checkout · $amount" (the founder's call, 2026-09-05). ——— */
    function refreshGiftPresentation() {
      var noteEl = q('[data-cdw-gift-note]');
      var name = gift.to.trim();
      var title = q('[data-cdw-title]');
      var giftOpen = gift.open;
      if (title) {
        title.textContent = (giftOpen && name) ? ('A gift for ' + name) : title.getAttribute('data-default-title');
      }
      var cardFor = q('[data-cdw-card-for]');
      if (cardFor) { cardFor.textContent = name ? ('For ' + name) : ''; cardFor.hidden = !name; }
      var railHeading = q('[data-cdw-rail-heading]');
      if (railHeading && !railHeading.hasAttribute('data-promo')) {
        var named = railHeading.getAttribute('data-heading-named') || '';
        var plain = railHeading.getAttribute('data-heading-plain') || '';
        railHeading.textContent = (giftOpen && name && named) ? named.split('{name}').join(name) : plain;
      }
      var counter = q('[data-cdw-gift-count]');
      if (counter && noteEl) counter.textContent = gift.note.length + ' / ' + (noteEl.getAttribute('maxlength') || 300);
    }

    /* "Order today, arrives by <weekday, Mon D>" — ship day = today if a
       business day before the warehouse cutoff (Utah time), else the next
       business day; arrival = ship day + N business days (the slow end of the
       window, so it is honest by construction). Same rule as the Gift Finder. */
    function arrivalLabel() {
      try {
        var maxDays = Math.max(1, parseInt(root.getAttribute('data-ship-days-max'), 10) || 5);
        var cutoff = parseInt(root.getAttribute('data-ship-cutoff-hour'), 10);
        if (isNaN(cutoff)) cutoff = 14;
        /* everything in the WAREHOUSE's calendar: Denver Y/M/D + hour drive a
           UTC-midnight walk, so a buyer in London at 00:30 (Denver 17:30 the
           day before) gets the right ship day (Opus lens-1 M2) */
        var parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', hour: 'numeric', hour12: false, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
        var y = 0, mo = 0, da = 0, hour = 0;
        parts.forEach(function (p) {
          if (p.type === 'year') y = parseInt(p.value, 10);
          if (p.type === 'month') mo = parseInt(p.value, 10);
          if (p.type === 'day') da = parseInt(p.value, 10);
          if (p.type === 'hour') hour = parseInt(p.value, 10) % 24;
        });
        var d = new Date(Date.UTC(y, mo - 1, da));
        var isBiz = function (x) { return x.getUTCDay() !== 0 && x.getUTCDay() !== 6; };
        var shipsToday = isBiz(d) && hour < cutoff;
        if (!shipsToday) { do { d.setUTCDate(d.getUTCDate() + 1); } while (!isBiz(d)); }
        var added = 0;
        while (added < maxDays) { d.setUTCDate(d.getUTCDate() + 1); if (isBiz(d)) added++; }
        return 'Order today, arrives by ' + d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
      } catch (_) { return ''; }
    }

    /* ——— wire everything inside the swappable inner ——— */
    function wireInner() {
      var inner = q('[data-cdw-inner]');
      if (!inner) return;
      if (!gift.seeded) seedGiftFromDom(inner);
      /* a bag carrying the retired attributes: migrate once (name into the note, keys
         cleared) — only when the gift fields rendered */
      if (!legacyGiftCleared && inner.hasAttribute('data-cdw-gift-legacy') && q('[data-cdw-gift-note]')) {
        legacyGiftCleared = true;
        migrateLegacyGift();
      }
      refreshGiftPresentation();
      var arrivalEl = q('[data-cdw-arrival]');
      if (arrivalEl) {
        var label = arrivalLabel();
        arrivalEl.textContent = label ? (label + ' · ') : '';
        arrivalEl.hidden = !label;
      }
      syncExpress();
      /* Pairing impression — GA4 standard promotion event; the dashboard's
         Ga4PromotionDaily sync ingests it with zero new code. Deduped per
         pairing per OPEN session so a qty change doesn't inflate impressions. */
      trackImpression();
    }

    /* the pairing impression: fired on open AND after every successful swap
       (the card is in the first server render — an impression must not depend
       on the SRA round-trip succeeding) */
    /* an impression is a card the buyer could SEE: inside the scroll body's box
       (and, on the mobile rail, inside the track's horizontal viewport) */
    function cardVisible(card) {
      var body = q('.cdw__body');
      if (!body) return false;
      var b = body.getBoundingClientRect();
      var r = card.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return false;
      var vertical = r.top < b.bottom - 24 && r.bottom > b.top + 24;
      var track = card.closest('[data-cdw-rail-track]');
      var horizontal = true;
      if (track) { var tb = track.getBoundingClientRect(); horizontal = r.left < tb.right - 24 && r.right > tb.left + 24; }
      return vertical && horizontal;
    }
    function trackImpression() {
      if (!isOpen()) return;
      var cards = root.querySelectorAll('[data-cdw-pair]');
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var pid = card.getAttribute('data-cdw-pair-id') || '';
        if (!pid || seenPromos[pid] || !cardVisible(card)) continue;
        seenPromos[pid] = true;
        track('view_promotion', {
          promotion_id: pid,
          promotion_name: card.getAttribute('data-cdw-pair-name') || '',
          creative_slot: card.getAttribute('data-cdw-pair-slot') || 'cart_drawer',
          creative_name: 'position_' + (i + 1),
          items: [{
            item_id: (pid.split('>')[1] || pid),
            item_name: card.getAttribute('data-cdw-pair-name') || ''
          }]
        });
      }
    }

    function submitCheckout(form, btn) {
      if (form.requestSubmit) { form.requestSubmit(btn); return; }
      /* form.submit() drops the submitter's name=checkout — add it by hand */
      var h = document.createElement('input');
      h.type = 'hidden'; h.name = 'checkout'; h.value = '';
      form.appendChild(h);
      form.submit();
    }

    /* Delegated events survive innerHTML swaps — bind once on root. */
    root.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var minus = e.target.closest('[data-cdw-minus]');
      var plus = e.target.closest('[data-cdw-plus]');
      var remove = e.target.closest('[data-cdw-remove]');
      var kitRemove = e.target.closest('[data-cdw-kit-remove]');
      var pairAdd = e.target.closest('[data-cdw-pair-add]');
      var closeBtn = e.target.closest('[data-cdw-close]');
      var checkout = e.target.closest('[data-cdw-checkout]');

      if (closeBtn) { close(); return; }
      if (minus || plus) {
        var row = (minus || plus).closest('[data-cdw-line]');
        if (!row) return;
        var input = row.querySelector('[data-cdw-qty-input]');
        var qty = parseInt(input && input.value, 10) || 0;
        qty = minus ? Math.max(0, qty - 1) : qty + 1;
        if (input) input.value = qty;
        var qtyBox = row.querySelector('.cdw__qty');
        if (qtyBox) qtyBox.setAttribute('aria-busy', 'true'); /* the swap replaces the node */
        changeLine(row.getAttribute('data-cdw-line'), qty);
        return;
      }
      if (remove) {
        var rrow = remove.closest('[data-cdw-line]');
        if (rrow) changeLine(rrow.getAttribute('data-cdw-line'), 0);
        return;
      }
      if (kitRemove) {
        var keys = (kitRemove.getAttribute('data-cdw-kit-remove') || '').split(',').filter(Boolean);
        if (keys.length) removeKit(keys);
        return;
      }
      if (pairAdd) {
        pairAdd.disabled = true;
        addVariant(pairAdd.getAttribute('data-cdw-pair-add'), pairAdd.getAttribute('data-cdw-pair-title'), {
          handle: pairAdd.getAttribute('data-cdw-pair-handle'),
          anchor: pairAdd.getAttribute('data-cdw-pair-anchor'),
          tier: pairAdd.getAttribute('data-cdw-pair-tier')
        }).then(restore, restore);
        function restore() {
          /* a successful swap replaced the node; if the refresh failed the
             same node is still here and must not stay dead */
          if (pairAdd.isConnected) pairAdd.disabled = false;
        }
        return;
      }
      var plainClick = !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0;
      if (checkout) {
        /* never lose a mid-debounce gift note to navigation — but never hang
           the buyer on it either: the flush races a short timeout, and the
           pagehide keepalive covers whatever is still in flight */
        var form = checkout.closest('form');
        if (form && plainClick && hasPendingNote()) {
          e.preventDefault();
          var timeout = new Promise(function (r) { window.setTimeout(r, CHECKOUT_FLUSH_TIMEOUT_MS); });
          Promise.race([flushNote(), timeout]).then(function () { submitCheckout(form, checkout); });
        }
        return;
      }
      var viewCart = e.target.closest('.cdw__viewcart a');
      if (viewCart && plainClick && hasPendingNote()) {
        e.preventDefault();
        var t2 = new Promise(function (r) { window.setTimeout(r, CHECKOUT_FLUSH_TIMEOUT_MS); });
        Promise.race([flushNote(), t2]).then(function () { window.location.href = viewCart.getAttribute('href'); });
      }
    });

    root.addEventListener('input', function (e) {
      if (!e.target.matches) return;
      if (e.target.matches('[data-cdw-gift-note]')) { readTypedNote(e.target); }
      else if (e.target.matches('[data-cdw-gift-to]')) { absorbAssignedNote(true); gift.to = e.target.value; }
      else return;
      queueGiftSave();
      refreshGiftPresentation();
    });

    /* a tap on a wallet button (Shop Pay / PayPal / Google Pay) leaves the page
       inside the note debounce — flush on the way in; the pagehide keepalive
       covers the request already in flight */
    root.addEventListener('pointerdown', function (e) {
      if (e.target.closest && e.target.closest('[data-cdw-express]') && hasPendingNote()) flushNote();
    }, true);

    root.addEventListener('compositionstart', function (e) {
      if (e.target.matches && e.target.matches('[data-cdw-gift-note], [data-cdw-gift-to]')) composing = true;
    }, true);
    root.addEventListener('compositionend', function (e) {
      if (e.target.matches && e.target.matches('[data-cdw-gift-note], [data-cdw-gift-to]')) {
        composing = false;
        if (e.target.matches('[data-cdw-gift-note]')) readTypedNote(e.target);
        else { absorbAssignedNote(true); gift.to = e.target.value; }
        queueGiftSave();
      }
    }, true);

    root.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('[data-cdw-qty-input]')) {
        var row = e.target.closest('[data-cdw-line]');
        var qty = Math.max(0, parseInt(e.target.value, 10) || 0);
        e.target.value = qty;
        if (row) changeLine(row.getAttribute('data-cdw-line'), qty);
      }
    });

    root.addEventListener('toggle', function (e) {
      if (e.target.matches && e.target.matches('[data-cdw-gift]')) {
        gift.open = e.target.open;
        refreshGiftPresentation();
      }
    }, true);

    scrim.addEventListener('click', close);
    /* scrolling the body or swiping the rail can bring more cards into view */
    var scrollTick = null;
    root.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = window.setTimeout(function () { scrollTick = null; trackImpression(); }, 200);
    }, true);

    /* Swipe-right-to-close from the HEADER only (Sol mobile #9): direction-
       consistent with a right drawer; never fights vertical cart scrolling
       (touch-action: pan-y scopes it); inert while a field is focused. The
       header is inside the swapped node, so this is delegated from the panel. */
    (function () {
      if (!window.PointerEvent) return;
      var startX = null, startY = null, startT = 0;
      panel.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'touch' || !e.target.closest || !e.target.closest('.cdw__head')) return;
        var ae = document.activeElement;
        if (ae && ae.matches && ae.matches('input, textarea')) return;
        startX = e.clientX; startY = e.clientY; startT = e.timeStamp;
      });
      panel.addEventListener('pointerup', function (e) {
        if (startX === null || e.pointerType !== 'touch') return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        var dt = Math.max(1, e.timeStamp - startT);
        startX = startY = null;
        if (Math.abs(dx) <= 1.5 * Math.abs(dy)) return; /* horizontal dominance */
        if (dx > 96 || (dx > 40 && dx / dt > 0.55)) close();
      });
      panel.addEventListener('pointercancel', function () { startX = startY = null; });
    })();

    root.addEventListener('focusin', function (e) {
      if (!e.target.matches || !e.target.matches('[data-cdw-gift-note], [data-cdw-gift-to]')) return;
      var field = e.target;
      /* center the field once the keyboard animation settles */
      window.setTimeout(function () {
        try {
          field.scrollIntoView({
            block: 'center',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        } catch (_) { /* older engines */ }
      }, 250);
    });

    wireInner();

    /* theme-editor section reload: tear down the old instance so its keydown
       trap and body scroll lock do not outlive the detached root */
    function destroy() {
      if (keydownHandler) { document.removeEventListener('keydown', keydownHandler); keydownHandler = null; }
      if (noteTimer) { window.clearTimeout(noteTimer); noteTimer = null; }
      lockScroll(false);
      mutationEpoch++; /* any in-flight refresh for this root is discarded */
    }

    return {
      panel: panel,
      open: open,
      close: close,
      refresh: refresh,
      refreshFromEvent: refreshFromEvent,
      isOpen: isOpen,
      flushNoteKeepalive: flushNoteKeepalive,
      destroy: destroy
    };
  }

  /* ——— globals: bound ONCE per page, resolve the live instance lazily ——— */
  var globalsWired = false;
  var hoverTimer = null;

  function wireGlobals() {
    if (globalsWired) return;
    globalsWired = true;

    /* the public event API */
    document.addEventListener('cart:open', function () { if (drawer) drawer.open({ refresh: true, trigger: 'add' }); });
    document.addEventListener('cart:refresh', function () { if (drawer) drawer.refreshFromEvent(); });

    /* header cart icon: click always opens; desktop hover-intent opens (unmeasured) */
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a.mini_cart');
      if (!link || !drawer) return;
      e.preventDefault();
      drawer.open({ refresh: true, trigger: 'icon' });
    });
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseover', function (e) {
        var container = e.target.closest && e.target.closest('.cart-container');
        if (!container || !drawer || drawer.isOpen() || hoverTimer) return;
        hoverTimer = window.setTimeout(function () {
          hoverTimer = null;
          if (drawer && !drawer.isOpen()) drawer.open({ refresh: true, trigger: 'hover' });
        }, 160);
      });
      document.addEventListener('mouseout', function (e) {
        var container = e.target.closest && e.target.closest('.cart-container');
        if (container && hoverTimer) { window.clearTimeout(hoverTimer); hoverTimer = null; }
      });
    }

    /* iOS keyboard mode (Sol mobile #3): while the keyboard is up the drawer
       resizes to the VISUAL viewport and the checkout footer yields. A ~150px
       shrink is the keyboard heuristic; URL-bar collapse is smaller. */
    if (window.visualViewport) {
      var vv = window.visualViewport;
      var syncVisualViewport = function () {
        if (!drawer) return;
        var panel = drawer.panel;
        if (!drawer.isOpen()) { panel.classList.remove('is-keyboard-open'); return; }
        var keyboardOpen = (window.innerHeight - vv.height) > 150;
        panel.style.setProperty('--cdw-visual-height', vv.height + 'px');
        panel.style.setProperty('--cdw-visual-top', vv.offsetTop + 'px');
        panel.classList.toggle('is-keyboard-open', keyboardOpen);
      };
      vv.addEventListener('resize', syncVisualViewport);
      vv.addEventListener('scroll', syncVisualViewport);
    }

    window.addEventListener('pagehide', function () { if (drawer) drawer.flushNoteKeepalive(); });

    /* two-tab truth: refresh on tab return */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && drawer && drawer.isOpen()) drawer.refresh();
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.cdwReady === 'true') return;
    root.dataset.cdwReady = 'true';
    var instance = createDrawer(root);
    if (instance) {
      if (drawer && drawer.destroy) drawer.destroy();
      drawer = instance;
    }
  }

  function initAll() {
    wireGlobals();
    var roots = document.querySelectorAll('[data-section-type="cart-drawer"]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  /* theme editor: only OUR section reload re-inits (a new root replaces the instance) */
  document.addEventListener('shopify:section:load', function (e) {
    var target = e && e.target;
    if (target && target.querySelector && target.querySelector('[data-section-type="cart-drawer"]')) initAll();
  });
})();
